export type Bearing = 1 | 2 | 3 | 4 | 5 | 6;

const validBearings: readonly Bearing[] = [1, 2, 3, 4, 5, 6];

function wrapIndex(i: number): number {
  let res = (i % 6);
  if (res < 0) {
    res = res + 6;
  }
  return res;
}

const HexBearing = {
  // compass direction based on hexgrid alpha = 0
  Q: 1 as Bearing, // east
  R: 2 as Bearing, // south southeast
  S: 3 as Bearing, // south southwest
  _Q: 4 as Bearing, // west
  _R: 5 as Bearing, // north northwest
  _S: 6 as Bearing, // north northeast

  all: validBearings,

  /**
   * returns the bearing representing a 180° rotation from the given bearing.
   */
  opposite(bearing: Bearing): Bearing {
    return this.next(bearing, 3);
  },



  /**
   * returns the "next" bearing in the infinite sequence of clockwise rotation.
   * @param {number} by - how many steps to rotate. default is 1
   */
  next(bearing: Bearing, by: number = 1): Bearing {
    const index = wrapIndex(validBearings.indexOf(bearing) + by);
    return validBearings[index];
  },


  /**
   * returns the "previous" bearing in the infinite sequence of clockwise rotation.
   * @param {number} by - how many steps to rotate. default is 1
   */
  previous(bearing: Bearing, by: number = 1): Bearing {
    return this.next(bearing, -by);
  },

  /**
   * Given two bearings, returns the one that is clockwise relative to the other.
   * If they are opposite (180 degrees), it falls back to the one with the lower numerical value.
   */
  getClockwiseWinner(b1: Bearing, b2: Bearing): Bearing {
    if (b1 === b2) return b1;

    const b1Index = validBearings.indexOf(b1);
    const b2Index = validBearings.indexOf(b2);
    const clockwiseDist = (b2Index - b1Index + 6) % 6;

    if (clockwiseDist === 3) { // 180-degree opposition
      return b1 < b2 ? b1 : b2;
    }

    // A clockwise distance of 1 or 2 means b2 is clockwise from b1.
    if (clockwiseDist > 0 && clockwiseDist < 3) {
      return b2;
    } else {
      // Otherwise (distance of 4 or 5), b1 is clockwise from b2.
      return b1;
    }
  },

  step(fromBearing: Bearing, toBearing: Bearing): Bearing {
    if (fromBearing === toBearing) {
      return fromBearing;
    }

    // `validBearings` is [1, 2, 3, 4, 5, 6]
    const fromIndex = validBearings.indexOf(fromBearing);
    const toIndex = validBearings.indexOf(toBearing);

    const clockwiseDist = (toIndex - fromIndex + 6) % 6;

    // A turn of 3 hex sides is 180 degrees. We'll define this as a "right" turn (clockwise).
    // Any clockwise distance of 1, 2, or 3 hex sides is shortest or tied by turning clockwise.
    if (clockwiseDist > 0 && clockwiseDist <= 3) {
      return this.next(fromBearing);
    } else {
      // Any clockwise distance of 4 or 5 is shorter to turn counter-clockwise.
      return this.previous(fromBearing);
    }
  }
};

export default HexBearing;
